import { useState, useEffect } from 'react';
import { fetchData, postData, updateData, fetchDoctorDetails } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Select from 'react-select';
import Button from '@mui/material/Button';
import { Paper, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

function WeeklySchedule() {
    const [daysOfWeek, setDaysOfWeek] = useState([]);

    const [staffOptions, setStaffOptions] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [staffId, setStaffId] = useState('');
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [showUpdateButton, setShowUpdateButton] = useState(false);
    const [showCreateButton, setShowCreateButton] = useState(false);
    const { isAuthenticated, companyId, token, userId, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchStaff();
    }, [isAuthenticated, companyId, token, userId, logout]);

    const fetchStaff = async () => {
        try {
            const response = await fetchData('DoctorApi', `/${companyId}`);
            if (response.responseCode === '1111') {
                const options = response.response_data.map(doctor => ({
                    value: doctor.Id,
                    label: doctor.Name,
                }));
                setStaffOptions(options);
            }
        } catch (error) {
            console.error('Error fetching doctor:', error);
        }
    };

    const handleStaffChange = async (selectedOption) => {
        setSelectedStaff(selectedOption);
        setStaffId(selectedOption.value);
        try {
            const response = await fetchDoctorDetails('WeeklyScheduleApi', companyId, selectedOption.value, token);
            if (response.responseCode === '1111' && response.response_data) {
                setDaysOfWeek(response.response_data); // for create purpose
                const anyDayAvailable = response.response_data.some(day => day.isAvailable);
                const allDaysUnavailable = response.response_data.every(day => !day.isAvailable);
                setShowUpdateButton(anyDayAvailable);
                setShowCreateButton(allDaysUnavailable);
            } else {
                console.error('Invalid response format', response);
            }
        } catch (error) {
            console.error('Error fetching days of the week:', error);
        }
    };

    const dayColors = {
        Sunday: 'red',
        Monday: 'darkgreen',
        Tuesday: 'darkgreen',
        Wednesday: 'darkgreen',
        Thursday: 'darkgreen',
        Friday: 'darkgreen',
        Saturday: 'darkred'
    };

    const handleCheckboxChange = (dayName) => {
        setDaysOfWeek(prevDaysOfWeek =>
            prevDaysOfWeek.map(day =>
                day.dayName === dayName ? { ...day, isAvailable: !day.isAvailable } : day
            )
        );
    };

    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async () => {
        setIsLoading(true);
        const updatedSchedule = daysOfWeek.map(day => ({
            CompanyId: companyId,
            scheduleId: day.scheduleId,
            DayID: day.dayId,
            dayName: day.dayName,
            isAvailable: day.isAvailable,
            CreatedBy: userId,
            DoctorId: staffId,
        }));

        const updatedScheduleData = { timings: updatedSchedule };

        try {
            const response = await postData('WeeklyScheduleApi', updatedScheduleData, token);
            console.log('WeeklySchedule Created success:', response);
            setSuccessMessage("WeeklySchedule Created successfully!");
            setErrorMessage("");
            window.location.reload();
        } catch (error) {
            console.error('Item Create error:', error);
            setErrorMessage("Failed to create the schedule. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };


    const handleUpdate = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        // Prepare the updated schedule data
        const updatedScheduleData = {
            timings: daysOfWeek.map(item => ({
                ...item,
                CompanyId: companyId,
                UpdatedBy: userId,
            })),
        };

        try {
            const response = await updateData(
                'WeeklyScheduleApi',
                staffId,
                updatedScheduleData,
                token
            );

            console.log('Record updated successfully:', response);
            setSuccessMessage("WeeklySchedule Updated successfully!");
            setErrorMessage("");
            setTimeout(() => {
                setSuccessMessage("");
                window.location.reload();
            }, 3000);
        } catch (error) {
            console.error('Schedule Update error:', error);
            setErrorMessage("Failed to update the schedule. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{
            // backgroundColor: '#f8f9fa', 
            padding: '15px',
            minHeight: '100vh',
            marginTop: '-45px'
        }}>
            <div style={{
                maxWidth: '650px',
                margin: '0 auto'
            }}>
                {/* Header Card */}
                <Paper elevation={2} style={{
                    padding: '15px 20px',
                    marginBottom: '15px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    height: '115px'
                }}>
                    <Typography variant="h6" style={{
                        color: 'white',
                        fontWeight: '600',
                        marginBottom: '12px',
                        fontSize: '14px'
                    }}>
                        Weekly Schedule Management
                    </Typography>

                    <div style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'flex-end',
                        flexWrap: 'wrap'
                    }}>
                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <label style={{
                                display: 'block',
                                color: 'white',
                                fontSize: '12px',
                                marginBottom: '6px',
                                fontWeight: '500'
                            }}>
                                Select Staff Member
                            </label>
                            <Select
                                value={selectedStaff}
                                onChange={handleStaffChange}
                                options={staffOptions}
                                placeholder="Choose staff..."
                                isSearchable
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '29px',
                                        height: '29px',
                                        borderRadius: '6px',
                                        border: 'none',
                                        fontSize: '12px'
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        height: '29px',
                                        fontSize: '12px',
                                        padding: '0 8px'
                                    }),
                                    input: (base) => ({
                                        ...base,
                                        margin: '0',
                                        padding: '0'
                                    }),
                                    indicatorsContainer: (base) => ({
                                        ...base,
                                        height: '29px',
                                        fontSize: '12px'
                                    }),
                                    placeholder: (base) => ({
                                        ...base,
                                        color: '#999'
                                    })
                                }}
                            />
                        </div>

                        {showUpdateButton && (
                            <Button
                                onClick={handleUpdate}
                                variant="contained"
                                style={{
                                    backgroundColor: 'white',
                                    color: '#667eea',
                                    fontWeight: '600',
                                    textTransform: 'none',
                                    padding: '0 18px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    height: '29px',
                                    minHeight: '29px',
                                    lineHeight: '29px'
                                }}
                                disabled={isLoading}
                            >
                                {isLoading ? <CircularProgress size={16} style={{ color: '#667eea' }} /> : "Update Schedule"}
                            </Button>
                        )}

                        {showCreateButton && (
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                style={{
                                    backgroundColor: 'white',
                                    color: '#667eea',
                                    fontWeight: '600',
                                    textTransform: 'none',
                                    padding: '0 18px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    height: '29px',
                                    minHeight: '29px',
                                    lineHeight: '29px'
                                }}
                                disabled={isLoading}
                            >
                                {isLoading ? <CircularProgress size={16} style={{ color: '#667eea' }} /> : "Create Schedule"}
                            </Button>
                        )}
                    </div>
                </Paper>

                {/* Schedule Card */}
                <Paper elevation={2} style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginTop: '-10px',
                }}>
                    <div style={{
                        padding: '12px 15px',
                        background: 'linear-gradient(135deg, #6ce5e9ff 0%, #764ba2 100%)',
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        <Typography variant="h6" style={{
                            color: '#333',
                            fontWeight: '600',
                            fontSize: '14px',
                            height: '10px',
                        }}>
                            Weekly Availability
                        </Typography>
                    </div>

                    <div style={{ padding: '10px' }}>
                        {Array.isArray(daysOfWeek) && daysOfWeek.length > 0 ? (
                            <div style={{ display: 'grid', gap: '8px' }}>
                                {daysOfWeek.map((item, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '10px 12px',
                                            backgroundColor: item.isAvailable ? '#f0f9ff' : '#fff',
                                            border: item.isAvailable ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                                            borderRadius: '6px',
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                            height: '55px',
                                            transform: 'scale(1)',
                                            boxShadow: item.isAvailable ? '0 2px 8px rgba(59, 130, 246, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                                        }}
                                        onClick={() => handleCheckboxChange(item.dayName)}
                                        onMouseDown={(e) => {
                                            e.currentTarget.style.transform = 'scale(0.97)';
                                            e.currentTarget.style.boxShadow = item.isAvailable ? '0 1px 4px rgba(59, 130, 246, 0.3)' : '0 0 2px rgba(0, 0, 0, 0.2)';
                                        }}
                                        onMouseUp={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)';
                                            e.currentTarget.style.boxShadow = item.isAvailable ? '0 2px 8px rgba(59, 130, 246, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)';
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'scale(1.02)';
                                            e.currentTarget.style.boxShadow = item.isAvailable ? '0 4px 12px rgba(59, 130, 246, 0.3)' : '0 2px 6px rgba(0, 0, 0, 0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)';
                                            e.currentTarget.style.boxShadow = item.isAvailable ? '0 2px 8px rgba(59, 130, 246, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)';
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                            <div style={{
                                                width: '32px',
                                                height: '30px',
                                                borderRadius: '6px',
                                                backgroundColor: dayColors[item.dayName] || '#999',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontWeight: 'bold',
                                                fontSize: '11px'
                                            }}>
                                                {item.dayName ? item.dayName.substring(0, 3).toUpperCase() : 'N/A'}
                                            </div>
                                            <div>
                                                <Typography style={{
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#1f2937'
                                                }}>
                                                    {item.dayName}
                                                </Typography>
                                                <Typography style={{
                                                    fontSize: '11px',
                                                    color: '#6b7280',
                                                    marginTop: '1px'
                                                }}>
                                                    {item.isAvailable ? 'Available for Appointment' : 'Not available'}
                                                </Typography>
                                            </div>
                                        </div>

                                        <div style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '4px',
                                            border: item.isAvailable ? '2px solid #3b82f6' : '2px solid #d1d5db',
                                            backgroundColor: item.isAvailable ? '#3b82f6' : 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s ease'
                                        }}>
                                            {item.isAvailable && (
                                                <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px 15px',
                                color: '#9ca3af'
                            }}>
                                <Typography variant="body2" style={{ fontSize: '13px' }}>
                                    Please select a staff member to view their schedule
                                </Typography>
                            </div>
                        )}
                    </div>
                </Paper>

                {/* Messages */}
                {errorMessage && (
                    <Paper elevation={1} style={{
                        marginTop: '12px',
                        padding: '10px 15px',
                        backgroundColor: '#fee2e2',
                        borderRadius: '6px',
                        border: '1px solid #fca5a5'
                    }}>
                        <Typography style={{ color: '#dc2626', fontWeight: '500', fontSize: '12px' }}>
                            ⚠️ {errorMessage}
                        </Typography>
                    </Paper>
                )}

                {successMessage && (
                    <Paper elevation={1} style={{
                        marginTop: '12px',
                        padding: '10px 15px',
                        backgroundColor: '#d1fae5',
                        borderRadius: '6px',
                        border: '1px solid #6ee7b7'
                    }}>
                        <Typography style={{ color: '#059669', fontWeight: '500', fontSize: '12px' }}>
                            ✓ {successMessage}
                        </Typography>
                    </Paper>
                )}
            </div>
        </div>
    );
}

export default WeeklySchedule;
